import {
    useLogStore,
} from '~~/stores/log'
import {
    useLoadingStore,
} from '~~/stores/loading'

export class RequestError extends Error {
    constructor(message: string, options?: { cause?: unknown }) {
        super(message, options)
        this.name = 'RequestError'
    }
}

const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred'

export function describeError(err: unknown): string {
    if (!(err instanceof RequestError)) {
        return UNEXPECTED_ERROR_MESSAGE
    }
    const response = err.cause as { statusMessage?: string, data?: { message?: string } } | undefined
    const serverMessage = response?.data?.message
    const statusText = response?.statusMessage
    return serverMessage || statusText || UNEXPECTED_ERROR_MESSAGE
}

export function isNotFoundRequestError(err: unknown): boolean {
    return err instanceof RequestError && (err.cause as any)?.data?.statusCode === 404
}

export const useRequest = async <T>({
    label,
    fetch,
    evaluate = (data) => data,
    store = (result) => {
    },
    successDetails = (_) => [],
    finalize = undefined,
    propagate = false,
    indicateBusy = false,
}) => {
    const logStore = useLogStore()
    const loadingStore = indicateBusy
        ? useLoadingStore()
        : undefined
    logStore.info(`${label} begin`)
    loadingStore?.begin()
    try {
        const data = await fetch()
        const result = evaluate(data)
        store(result)
        const details = successDetails(result)
        const detailsArray = Array.isArray(details)
            ? details
            : [details]
        logStore.info(`${label} end`, ...detailsArray)
        return result
    }
    catch (err) {
        if (err instanceof RequestError) {
            throw err
        }
        const errData = (err as any)?.data?.data
        const details = [
            errData?.reason ? `reason: ${errData.reason}` : null,
            errData?.error_description ? `description: ${errData.error_description}` : null,
        ].filter(Boolean)
        logStore.error(`${label} failed`, ...(details.length ? details : [err]))
        if (propagate) {
            throw new RequestError((err as any)?.message ?? `${label} failed`, { cause: err })
        }
    }
    finally {
        loadingStore?.end()
        if (finalize) {
            finalize()
        }
    }
}
